import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Check as CheckIcon, Error as ErrorIcon } from '@mui/icons-material';
import { RichTextEditor } from './RichTextEditor';
import { ColorPicker } from './ColorPicker';
import { GameDateTimePicker } from './GameDateTimePicker';
import { boardsApi } from '../api';
import { useTierConfig, useNumberInput } from '../hooks';
import type { Sport, NumberMode, CellMode, CellShape } from '../types';
import { SPORTS } from '../types';

export interface BoardBasicInfo {
  name: string;
  slug: string;
  description: string;
  sport: Sport;
  homeTeam: string;
  awayTeam: string;
  homeColor: string;
  awayColor: string;
  gameDate: string;
  pricePerSquare: number;
  quarters: number;
  numberMode: NumberMode;
  cellMode?: CellMode;   // "standard" | "paired" | "quad"
  cellShape?: CellShape; // "1x1" | "2x1" | "1x2" | "2x2"
}

// Generate a URL-safe slug from text
// eslint-disable-next-line react-refresh/only-export-components
export const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove non-alphanumeric chars except spaces and dashes
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/-+/g, '-') // Collapse multiple dashes
    .replace(/^-|-$/g, '') // Remove leading/trailing dashes
    .slice(0, 50); // Max 50 chars
};

export interface BoardSettingsFormProps {
  values: BoardBasicInfo;
  onChange: (values: BoardBasicInfo) => void;
  mode: 'create' | 'edit';
  boardStatus?: 'draft' | 'live' | 'locked' | 'completed';
  showName?: boolean;
  showSlug?: boolean;
  showDescription?: boolean;
  showTeams?: boolean;
  showTeamColors?: boolean;
  showSport?: boolean;
  showQuarters?: boolean;
  showGameDate?: boolean;
  showPricePerSquare?: boolean;
  showNumberMode?: boolean;
  showCellMode?: boolean;
  lockCellMode?: boolean; // When true, cell mode cannot be changed (e.g., template with assignments selected)
  isLiveGame?: boolean;   // When true, team names are locked (set by live game selection)
}

// Get default quarters for sport
const getDefaultQuarters = (sportType: Sport): number => {
  switch (sportType) {
    case 'hockey':
      return 3;
    case 'soccer':
      return 2;
    default:
      return 4;
  }
};

// Get default quarter payouts for a number of quarters
// eslint-disable-next-line react-refresh/only-export-components
export const getDefaultQuarterPayouts = (quarters: number): Record<string, number> => {
  if (quarters === 2) {
    return { '1H': 0.3, '2H': 0.7 };
  }
  if (quarters === 3) {
    return { 'P1': 0.2, 'P2': 0.2, 'P3': 0.6 };
  }
  return { 'Q1': 0.1, 'Q2': 0.2, 'Q3': 0.15, 'Q4': 0.55 };
};

export const BoardSettingsForm: React.FC<BoardSettingsFormProps> = ({
  values,
  onChange,
  mode,
  boardStatus = 'draft',
  showName = true,
  showSlug = false,
  showDescription = true,
  showTeams = true,
  showTeamColors = true,
  showSport = true,
  showQuarters = true,
  showGameDate = true,
  showPricePerSquare = true,
  showNumberMode = true,
  showCellMode = true,
  lockCellMode = false,
  isLiveGame = false,
}) => {
  const isReadOnly = mode === 'edit' && boardStatus !== 'draft';
  const isTeamNamesReadOnly = (mode === 'edit' && boardStatus !== 'draft') || isLiveGame;
  const isSportReadOnly = mode === 'edit'; // Can't change sport after creation
  const isQuartersReadOnly = mode === 'edit'; // Can't change quarters after creation
  const isPriceReadOnly = mode === 'edit' && boardStatus !== 'draft';
  const isSlugReadOnly = mode === 'edit' && boardStatus !== 'draft';
  const isNumberModeReadOnly = mode === 'edit'; // Can't change number mode after creation
  // Cell mode can be changed until board is published, or locked when template with assignments is selected
  const isCellModeReadOnly = (mode === 'edit' && boardStatus !== 'draft') || lockCellMode;

  // Tier config for credit costs
  const { getCreditCost, getDisplayName } = useTierConfig();

  // Handle price per square changes from number input hook
  const handlePricePerSquareChange = useCallback((value: number) => {
    onChange({ ...values, pricePerSquare: value });
  }, [onChange, values]);

  // Use number input hook for better typing UX (allows clearing, validates on blur)
  const pricePerSquareInput = useNumberInput(values.pricePerSquare, handlePricePerSquareChange, { min: 1, defaultValue: 1, allowDecimals: false });

  // Slug availability state
  const [slugChecking, setSlugChecking] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const slugCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check slug availability with debounce
  useEffect(() => {
    // Clear any pending check
    if (slugCheckTimeoutRef.current) {
      clearTimeout(slugCheckTimeoutRef.current);
    }

    // Reset state if slug is empty
    if (!values.slug || !showSlug) {
      setSlugAvailable(null);
      setSlugChecking(false);
      return;
    }

    // Skip if slug is read-only (editing existing board)
    if (isSlugReadOnly) {
      setSlugAvailable(null);
      setSlugChecking(false);
      return;
    }

    // Debounce the check
    setSlugChecking(true);
    slugCheckTimeoutRef.current = setTimeout(async () => {
      try {
        const available = await boardsApi.checkSlugAvailable(values.slug);
        setSlugAvailable(available);
      } catch {
        // On error, assume available (server will validate)
        setSlugAvailable(null);
      } finally {
        setSlugChecking(false);
      }
    }, 500);

    return () => {
      if (slugCheckTimeoutRef.current) {
        clearTimeout(slugCheckTimeoutRef.current);
      }
    };
  }, [values.slug, showSlug, isSlugReadOnly]);

  const handleChange = <K extends keyof BoardBasicInfo>(field: K, value: BoardBasicInfo[K]) => {
    onChange({ ...values, [field]: value });
  };

  // Handle name change - auto-generate slug placeholder
  const handleNameChange = (name: string) => {
    onChange({
      ...values,
      name,
      // Only auto-update slug if it's empty or matches the generated slug from the old name
      slug: values.slug === '' || values.slug === generateSlug(values.name)
        ? generateSlug(name)
        : values.slug,
    });
  };

  // Handle slug change - validate and sanitize
  const handleSlugChange = (slug: string) => {
    // Only allow lowercase alphanumeric and dashes
    const sanitized = slug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    handleChange('slug', sanitized.slice(0, 50));
  };

  // Handle sport change - auto-update quarters
  const handleSportChange = (newSport: Sport) => {
    const defaultQuarters = getDefaultQuarters(newSport);
    onChange({
      ...values,
      sport: newSport,
      quarters: defaultQuarters,
    });
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Board Name */}
        {showName && (
          <Grid size={{ xs: 12, sm: showSlug ? 6 : 12 }}>
            <TextField
              fullWidth
              label="Board Name"
              value={values.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., Super Bowl 2024 Squares"
              required
              disabled={isReadOnly}
              inputProps={{ 'data-testid': 'board-name-input' }}
            />
          </Grid>
        )}

        {/* Custom Slug */}
        {showSlug && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Custom URL (Optional)"
              value={values.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder={generateSlug(values.name) || 'my-board-name'}
              disabled={isSlugReadOnly}
              error={slugAvailable === false}
              helperText={
                slugAvailable === false
                  ? 'This URL is already taken. Please choose a different one.'
                  : 'Letters, numbers, and dashes only. Max 50 chars.'
              }
              inputProps={{ maxLength: 50, 'data-testid': 'board-slug-input' }}
              InputProps={{
                endAdornment: values.slug && !isSlugReadOnly ? (
                  <InputAdornment position="end">
                    {slugChecking ? (
                      <CircularProgress size={20} />
                    ) : slugAvailable === true ? (
                      <CheckIcon color="success" />
                    ) : slugAvailable === false ? (
                      <ErrorIcon color="error" />
                    ) : null}
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
        )}

        {/* Description */}
        {showDescription && (
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Description (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Add payment instructions, rules, or any other details for participants.
            </Typography>
            <RichTextEditor
              value={values.description}
              onChange={(val) => handleChange('description', val)}
              maxLength={250}
              placeholder="e.g., Payment via Venmo @username or PayPal..."
              minRows={2}
            />
          </Grid>
        )}

        {/* Sport */}
        {showSport && (
          <Grid size={{ xs: 12, sm: 6 }}>
            {isSportReadOnly ? (
              <TextField
                fullWidth
                label="Sport"
                value={SPORTS.find((s) => s.value === values.sport)?.label || values.sport}
                disabled
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Sport</InputLabel>
                <Select
                  value={values.sport}
                  label="Sport"
                  onChange={(e) => handleSportChange(e.target.value as Sport)}
                  data-testid="board-sport-select"
                >
                  {SPORTS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
        )}

        {/* Scoring Periods */}
        {showQuarters && (
          <Grid size={{ xs: 12, sm: 6 }}>
            {isQuartersReadOnly ? (
              <TextField
                fullWidth
                label="Scoring Periods"
                value={
                  values.quarters === 2
                    ? '2 Halves (Soccer)'
                    : values.quarters === 3
                      ? '3 Periods (Hockey)'
                      : '4 Quarters (Football/Basketball)'
                }
                disabled
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Scoring Periods</InputLabel>
                <Select
                  value={values.quarters}
                  label="Scoring Periods"
                  onChange={(e) => handleChange('quarters', Number(e.target.value))}
                  data-testid="board-quarters-select"
                >
                  <MenuItem value={2}>2 Halves (Soccer)</MenuItem>
                  <MenuItem value={3}>3 Periods (Hockey)</MenuItem>
                  <MenuItem value={4}>4 Quarters (Football/Basketball)</MenuItem>
                </Select>
              </FormControl>
            )}
          </Grid>
        )}

        {/* Number Mode and Cell Mode - side by side */}
        {/* Number Mode */}
        {showNumberMode && (
          <Grid size={{ xs: 12, sm: 6 }}>
            {isNumberModeReadOnly ? (
              <TextField
                fullWidth
                label="Number Sets"
                value={getDisplayName(values.numberMode)}
                disabled
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Number Sets</InputLabel>
                <Select
                  value={values.numberMode}
                  label="Number Sets"
                  onChange={(e) => handleChange('numberMode', e.target.value as NumberMode)}
                  data-testid="board-number-mode-select"
                >
                  <MenuItem value="single">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>Same for All Periods</span>
                      <Chip label={`${getCreditCost('single')} Credit`} size="small" variant="outlined" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="half">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>Different per Half</span>
                      <Chip label={`${getCreditCost('half')} Credits`} size="small" color="primary" variant="outlined" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="quarter">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>Different per Quarter</span>
                      <Chip label={`${getCreditCost('quarter')} Credits`} size="small" color="secondary" variant="outlined" />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            )}
            <Typography variant="caption" color="text.secondary">
              {values.numberMode === 'single'
                ? 'One set of numbers for the entire game'
                : values.numberMode === 'half'
                  ? 'Two different number sets (1H and 2H)'
                  : 'Different numbers for each quarter/period'}
            </Typography>
          </Grid>
        )}

        {/* Cell Mode (Participation Mode) */}
        {showCellMode && (
          <Grid size={{ xs: 12, sm: 6 }}>
            {isCellModeReadOnly ? (
              <TextField
                fullWidth
                label="Participation Mode"
                value={
                  values.cellMode === 'quad' ? '25 Cells (Quad)' :
                  values.cellMode === 'paired' ? '50 Cells (Paired)' :
                  '100 Squares (Standard)'
                }
                disabled
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Participation Mode</InputLabel>
                <Select
                  value={`${values.cellMode || 'standard'}-${values.cellShape || '1x1'}`}
                  label="Participation Mode"
                  onChange={(e) => {
                    const [cellMode, cellShape] = e.target.value.split('-') as [CellMode, CellShape];
                    onChange({ ...values, cellMode, cellShape });
                  }}
                  data-testid="board-cell-mode-select"
                >
                  <MenuItem value="standard-1x1">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>100 Squares (Standard)</span>
                      <Chip label="100 participants" size="small" variant="outlined" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="paired-2x1">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>50 Cells - Vertical Pairs</span>
                      <Chip label="50 participants" size="small" color="primary" variant="outlined" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="paired-1x2">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>50 Cells - Horizontal Pairs</span>
                      <Chip label="50 participants" size="small" color="primary" variant="outlined" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="quad-2x2">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>25 Cells (Quad)</span>
                      <Chip label="25 participants" size="small" color="secondary" variant="outlined" />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            )}
            <Typography variant="caption" color="text.secondary">
              {values.cellMode === 'quad'
                ? `Each participant owns 4 squares (2x2). Price per cell: $${(values.pricePerSquare * 4).toFixed(2)}`
                : values.cellMode === 'paired'
                  ? `Each participant owns 2 squares. Price per cell: $${(values.pricePerSquare * 2).toFixed(2)}`
                  : 'Each participant owns 1 square'}
            </Typography>
          </Grid>
        )}

        {/* Price Per Square */}
        {showPricePerSquare && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Price Per Square"
              type="number"
              value={pricePerSquareInput.value}
              onChange={pricePerSquareInput.onChange}
              onBlur={pricePerSquareInput.onBlur}
              InputProps={{ startAdornment: '$' }}
              disabled={isPriceReadOnly}
              inputProps={{ 'data-testid': 'board-price-input' }}
            />
            {isPriceReadOnly && (
              <Typography variant="caption" color="text.secondary">
                Price cannot be changed after publishing
              </Typography>
            )}
          </Grid>
        )}

        {/* Teams and Game Date Section */}
        {showTeams && (
          <>
            {isTeamNamesReadOnly && (
              <Grid size={12}>
                <Alert severity="info" sx={{ mb: 0 }}>
                  {isLiveGame
                    ? 'Team names are set by the live game and cannot be changed.'
                    : <>Team names cannot be changed after the board is published.{showTeamColors && ' Colors can still be adjusted.'}</>
                  }
                </Alert>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: showGameDate ? 4 : 6 }}>
              <TextField
                fullWidth
                label="Team A (Home)"
                value={values.homeTeam}
                onChange={(e) => handleChange('homeTeam', e.target.value)}
                required
                disabled={isTeamNamesReadOnly}
                inputProps={{ 'data-testid': 'board-home-team-input' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: showGameDate ? 4 : 6 }}>
              <TextField
                fullWidth
                label="Team B (Away)"
                value={values.awayTeam}
                onChange={(e) => handleChange('awayTeam', e.target.value)}
                required
                disabled={isTeamNamesReadOnly}
                inputProps={{ 'data-testid': 'board-away-team-input' }}
              />
            </Grid>
            {/* Game Date - on same row as teams */}
            {showGameDate && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <GameDateTimePicker
                  value={values.gameDate}
                  onChange={(val) => handleChange('gameDate', val)}
                  required
                  disabled={isReadOnly || isLiveGame}
                />
              </Grid>
            )}
            {showTeamColors && (
              <>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ColorPicker
                    value={values.homeColor}
                    onChange={(val) => handleChange('homeColor', val)}
                    label="Team A Color"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ColorPicker
                    value={values.awayColor}
                    onChange={(val) => handleChange('awayColor', val)}
                    label="Team B Color"
                  />
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};
